import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { Row, DocumentoLista, DocumentoFull } from "../types";
import { api, ApiError } from "../api";
import { ordenarItens } from "../ordem";
import ErrorState from "./ErrorState";
import { IconPlus, IconClose, IconTrash, IconEye, IconDownload } from "../icons";
import { usePodeExcluir } from "../permissoes";

interface Props {
  itens: Row[];
  user: string;
  displayName: string | null;
  onDataChanged: () => void;
}

/* ============================ Helpers ============================ */

/** Data de hoje no fuso local (YYYY-MM-DD) para o valor inicial dos campos date. */
function hojeLocal(): string {
  const d = new Date();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${mm}-${dd}`;
}

function fmtData(s: string | null): string {
  if (!s) return "—";
  const d = new Date(s.length <= 10 ? s + "T00:00:00" : s);
  return isNaN(d.getTime()) ? s : d.toLocaleDateString("pt-BR");
}

/** Carrega a imagem do arquivo, reduz (máx 1280px) e devolve um JPEG dataURL. */
function compressImage(file: File, max = 1280, quality = 0.7): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      let { width, height } = img;
      if (width > max || height > max) {
        const s = Math.min(max / width, max / height);
        width = Math.round(width * s);
        height = Math.round(height * s);
      }
      const c = document.createElement("canvas");
      c.width = width;
      c.height = height;
      const ctx = c.getContext("2d");
      if (!ctx) return reject(new Error("Canvas indisponível"));
      ctx.drawImage(img, 0, 0, width, height);
      resolve(c.toDataURL("image/jpeg", quality));
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Não foi possível ler a imagem."));
    };
    img.src = url;
  });
}

const STATUS_PILL: Record<string, string> = {
  "Em posse": "pill-warn",
  Devolvido: "pill-ok",
  Consumido: "pill-neutral",
};

/* ==================== Captura de assinatura ==================== */
function SignaturePad({
  label,
  onChange,
}: {
  label: string;
  onChange: (v: string) => void;
}) {
  const ref = useRef<HTMLCanvasElement>(null);
  const drawing = useRef(false);
  const last = useRef<{ x: number; y: number } | null>(null);
  const [vazio, setVazio] = useState(true);

  const ctxOf = () => {
    const c = ref.current!;
    const ctx = c.getContext("2d")!;
    ctx.lineWidth = 2.2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#111827";
    return ctx;
  };

  const pos = (e: React.PointerEvent) => {
    const c = ref.current!;
    const r = c.getBoundingClientRect();
    return {
      x: ((e.clientX - r.left) * c.width) / r.width,
      y: ((e.clientY - r.top) * c.height) / r.height,
    };
  };

  const down = (e: React.PointerEvent) => {
    e.preventDefault();
    drawing.current = true;
    last.current = pos(e);
    (e.currentTarget as Element).setPointerCapture(e.pointerId);
  };
  const move = (e: React.PointerEvent) => {
    if (!drawing.current) return;
    const ctx = ctxOf();
    const p = pos(e);
    ctx.beginPath();
    ctx.moveTo(last.current!.x, last.current!.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    last.current = p;
  };
  const up = () => {
    if (!drawing.current) return;
    drawing.current = false;
    setVazio(false);
    onChange(ref.current!.toDataURL("image/png"));
  };
  const limpar = () => {
    const c = ref.current!;
    c.getContext("2d")!.clearRect(0, 0, c.width, c.height);
    setVazio(true);
    onChange("");
  };

  return (
    <div className="field field-wide">
      <span>
        {label} <span className="req">*</span>
        <button type="button" className="sig-clear" onClick={limpar} disabled={vazio}>
          Limpar
        </button>
      </span>
      <canvas
        ref={ref}
        width={520}
        height={170}
        className="sig-canvas"
        onPointerDown={down}
        onPointerMove={move}
        onPointerUp={up}
        onPointerLeave={up}
      />
      <small className="muted">Assine no quadro acima (dedo ou mouse).</small>
    </div>
  );
}

/* ==================== Captura de foto ==================== */
function PhotoCapture({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [erro, setErro] = useState<string | null>(null);

  const onFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // permite escolher o mesmo arquivo de novo
    if (!file) return;
    setErro(null);
    try {
      onChange(await compressImage(file));
    } catch (err) {
      setErro(err instanceof Error ? err.message : "Erro ao processar a foto.");
    }
  };

  return (
    <div className="field field-wide">
      <span>
        {label} <span className="req">*</span>
      </span>
      {value ? (
        <div className="photo-preview">
          <img src={value} alt={label} />
          <div className="photo-actions">
            <button type="button" className="btn-secondary btn-sm" onClick={() => inputRef.current?.click()}>
              Trocar
            </button>
            <button type="button" className="btn-secondary btn-sm btn-danger-text" onClick={() => onChange("")}>
              Remover
            </button>
          </div>
        </div>
      ) : (
        <button type="button" className="photo-btn" onClick={() => inputRef.current?.click()}>
          📷 Tirar / enviar foto
        </button>
      )}
      {erro && <div className="form-error">{erro}</div>}
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        capture="environment"
        hidden
        onChange={onFile}
      />
    </div>
  );
}

/**
 * REGISTRO FOTOGRÁFICO — várias fotos da entrega, cada uma com a sua legenda.
 *
 * O formulário oficial da SERCAMP tem uma seção de fotos em grade; é ela que o
 * termo gerado preenche (ver server/termo-docx.ts). A legenda vira o texto
 * "Figura N - …" no documento, então vale escrever algo que se entenda depois:
 * "material separado", "conferência com o colaborador".
 */
function GaleriaFotos({
  fotos,
  onChange,
}: {
  fotos: { imagem: string; legenda: string }[];
  onChange: (f: { imagem: string; legenda: string }[]) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [erro, setErro] = useState<string | null>(null);
  const MAX = 12;

  const onFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const arquivos = Array.from(e.target.files ?? []);
    e.target.value = "";
    if (arquivos.length === 0) return;
    setErro(null);
    try {
      const novas: { imagem: string; legenda: string }[] = [];
      for (const file of arquivos) {
        if (fotos.length + novas.length >= MAX) break;
        novas.push({ imagem: await compressImage(file), legenda: "" });
      }
      onChange([...fotos, ...novas]);
    } catch (err) {
      setErro(err instanceof Error ? err.message : "Erro ao processar a foto.");
    }
  };

  return (
    <div className="field field-wide">
      <span>
        Registro fotográfico da entrega{" "}
        <small className="muted">(entram no termo, em grade — máx. {MAX})</small>
      </span>

      {fotos.length > 0 && (
        <div className="galeria-fotos">
          {fotos.map((f, i) => (
            <div key={i} className="galeria-item">
              <img src={f.imagem} alt={`Figura ${i + 1}`} />
              <input
                className="galeria-legenda"
                placeholder={`Figura ${i + 1} — legenda`}
                value={f.legenda}
                onChange={(e) => {
                  const copia = [...fotos];
                  copia[i] = { ...copia[i], legenda: e.target.value };
                  onChange(copia);
                }}
              />
              <button
                type="button"
                className="btn-secondary btn-sm btn-danger-text"
                onClick={() => onChange(fotos.filter((_, x) => x !== i))}
              >
                Remover
              </button>
            </div>
          ))}
        </div>
      )}

      {fotos.length < MAX && (
        <button type="button" className="photo-btn" onClick={() => inputRef.current?.click()}>
          📷 Adicionar foto{fotos.length ? "s" : ""} da entrega
        </button>
      )}
      {erro && <div className="form-error">{erro}</div>}
      <input ref={inputRef} type="file" accept="image/*" capture="environment" multiple hidden onChange={onFile} />
    </div>
  );
}

/* ============================ Tela ============================ */
export default function DocumentosView({ itens, user, displayName, onDataChanged }: Props) {
  const podeExcluir = usePodeExcluir(); // só admin apaga termo já assinado
  const [rows, setRows] = useState<DocumentoLista[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiError | null>(null);
  const [novo, setNovo] = useState(false);
  const [ver, setVer] = useState<number | null>(null);
  const [devolver, setDevolver] = useState<DocumentoLista | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setRows(await api.documentos.list());
    } catch (err) {
      setError(err instanceof ApiError ? err : new ApiError(String(err)));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const aposMudar = () => {
    load();
    onDataChanged(); // atualiza saldo/itens e alertas no App
  };

  async function excluir(d: DocumentoLista) {
    if (!confirm(`Excluir o documento #${d.id}? Isso REVERTE a movimentação de estoque.`)) return;
    try {
      await api.documentos.remove(d.id);
      aposMudar();
    } catch (err) {
      alert(err instanceof ApiError ? err.message : "Erro ao excluir.");
    }
  }

  if (error) return <ErrorState message={error.message} code={error.code} onRetry={load} />;

  const responsavelPadrao = displayName || user;

  return (
    <div className="documentos">
      <div className="doc-head">
        <p className="muted">
          Cada saída gera um termo assinado (foto + assinaturas). Retornável fica "Em posse" até a devolução;
          consumível fecha na entrega.
        </p>
        <button className="btn-primary" onClick={() => setNovo(true)}>
          <IconPlus size={16} /> Novo documento
        </button>
      </div>

      <div className="table-wrap">
        {loading ? (
          <div className="empty">Carregando documentos...</div>
        ) : rows.length === 0 ? (
          <div className="empty">Nenhum documento ainda. Clique em "Novo documento" ao entregar um material.</div>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>#</th>
                <th>Item</th>
                <th>Colaborador</th>
                <th>Tipo</th>
                <th style={{ textAlign: "right" }}>Qtd</th>
                <th>Entrega</th>
                <th>Devolução</th>
                <th>Status</th>
                <th className="col-actions">Ações</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((d) => (
                <tr key={d.id}>
                  <td>{d.id}</td>
                  <td>
                    <div className="doc-item-nome">{d.item_descricao ?? "—"}</div>
                    {d.item_codigo && <small className="muted">{d.item_codigo}</small>}
                  </td>
                  <td>{d.colaborador}</td>
                  <td>{d.tipo_item}</td>
                  <td style={{ textAlign: "right" }}>
                    {d.quantidade} {d.item_unidade ?? ""}
                  </td>
                  <td>{fmtData(d.data_entrega)}</td>
                  <td>{fmtData(d.data_devolucao)}</td>
                  <td>
                    <span className={`pill ${STATUS_PILL[d.status] ?? "pill-neutral"}`}>{d.status}</span>
                  </td>
                  <td className="col-actions">
                    <div className="doc-acoes">
                      <button className="icon-btn" title="Ver documento" onClick={() => setVer(d.id)}>
                        <IconEye size={17} />
                      </button>
                      {/* O termo é gerado na hora, no formulário oficial da
                          SERCAMP, com as fotos e as assinaturas do documento. */}
                      <a
                        className="icon-btn"
                        title="Baixar termo preenchido (Word)"
                        href={api.documentos.termoUrl(d.id)}
                        download
                      >
                        <IconDownload size={17} />
                      </a>
                      {d.status === "Em posse" && (
                        <button className="btn-secondary btn-sm" onClick={() => setDevolver(d)}>
                          Devolver
                        </button>
                      )}
                      {podeExcluir && (
                        <button
                          className="icon-btn icon-btn-danger"
                          title="Excluir (reverte o estoque)"
                          onClick={() => excluir(d)}
                        >
                          <IconTrash size={17} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {novo && (
        <NovoDocumentoModal
          itens={itens}
          responsavelPadrao={responsavelPadrao}
          onClose={() => setNovo(false)}
          onSaved={() => {
            setNovo(false);
            aposMudar();
          }}
        />
      )}

      {ver != null && <VerDocumentoModal id={ver} onClose={() => setVer(null)} />}

      {devolver && (
        <DevolverModal
          doc={devolver}
          onClose={() => setDevolver(null)}
          onSaved={() => {
            setDevolver(null);
            aposMudar();
          }}
        />
      )}
    </div>
  );
}

/* ==================== Modal: novo documento (entrega) ==================== */
/**
 * Também é reutilizado ao ATENDER uma solicitação da Gestão da Operação: nesse
 * caso item/quantidade/colaborador chegam pré-preenchidos e `separacaoId` liga o
 * termo à solicitação (que o backend marca como "Atendida").
 */
export function NovoDocumentoModal({
  itens,
  responsavelPadrao,
  onClose,
  onSaved,
  itemInicial,
  quantidadeInicial,
  colaboradorInicial,
  separacaoId,
  titulo = "Novo documento de retirada",
  textoBotao = "Registrar entrega",
}: {
  itens: Row[];
  responsavelPadrao: string;
  onClose: () => void;
  onSaved: () => void;
  itemInicial?: string;
  quantidadeInicial?: string;
  colaboradorInicial?: string;
  separacaoId?: number;
  titulo?: string;
  textoBotao?: string;
}) {
  const [itemId, setItemId] = useState(itemInicial ?? "");
  const [quantidade, setQuantidade] = useState(quantidadeInicial ?? "1");
  const [tipoItem, setTipoItem] = useState<"Retornável" | "Consumível">("Retornável");
  const [colaborador, setColaborador] = useState(colaboradorInicial ?? "");
  const [responsavel, setResponsavel] = useState(responsavelPadrao);
  const [dataEntrega, setDataEntrega] = useState(hojeLocal());
  const [observacao, setObservacao] = useState("");
  const [foto, setFoto] = useState("");
  const [assResp, setAssResp] = useState("");
  const [assColab, setAssColab] = useState("");
  // Campos do FORMULÁRIO OFICIAL (o termo em Word é montado com eles).
  const [notaFiscal, setNotaFiscal] = useState("");
  const [matricula, setMatricula] = useState("");
  const [autorizadoPor, setAutorizadoPor] = useState(responsavelPadrao);
  const [fotos, setFotos] = useState<{ imagem: string; legenda: string }[]>([]);
  const [saving, setSaving] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const itemSel = itens.find((i) => String(i.id) === itemId);
  const saldo = itemSel ? Number(itemSel.saldo) : null;
  // Select de item em ordem alfabética pela descrição (cópia; `itens` intacto).
  const itensOrdenados = useMemo(() => ordenarItens(itens), [itens]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setErro(null);
    if (!itemId) return setErro("Selecione o item.");
    if (!(Number(quantidade) > 0)) return setErro("Informe uma quantidade maior que zero.");
    if (saldo != null && Number(quantidade) > saldo) return setErro(`Saldo insuficiente. Disponível: ${saldo}.`);
    if (!colaborador.trim()) return setErro("Informe o colaborador que está pegando o item.");
    if (!foto) return setErro("Capture a foto do item.");
    if (!assResp) return setErro("Capture a assinatura do responsável.");
    if (!assColab) return setErro("Capture a assinatura do colaborador.");

    setSaving(true);
    try {
      await api.documentos.create({
        item_id: Number(itemId),
        quantidade: Number(quantidade),
        tipo_item: tipoItem,
        colaborador: colaborador.trim(),
        responsavel: responsavel.trim(),
        data_entrega: dataEntrega,
        observacao: observacao.trim(),
        foto_entrega: foto,
        assinatura_responsavel: assResp,
        assinatura_colaborador: assColab,
        nota_fiscal: notaFiscal.trim(),
        solicitante: colaborador.trim(),
        matricula: matricula.trim(),
        autorizado_por: autorizadoPor.trim(),
        fotos: fotos.filter((f) => f.imagem),
        ...(separacaoId ? { separacao_id: separacaoId } : {}),
      });
      onSaved();
    } catch (err) {
      setErro(err instanceof ApiError ? err.message : "Erro ao salvar o documento.");
      setSaving(false);
    }
  }

  return (
    <div className="modal-overlay" onMouseDown={onClose}>
      <div className="modal-card modal-doc" onMouseDown={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <h3>{titulo}</h3>
          <button type="button" className="icon-btn" onClick={onClose}>
            <IconClose size={18} />
          </button>
        </div>
        <form className="modal-body doc-form" onSubmit={submit}>
          <div className="doc-grid">
            <label className="field field-wide">
              <span>Item <span className="req">*</span></span>
              <select value={itemId} onChange={(e) => setItemId(e.target.value)}>
                <option value="">Selecione o item...</option>
                {itensOrdenados.map((i) => (
                  <option key={i.id} value={i.id}>
                    {i.codigo} — {i.descricao}
                  </option>
                ))}
              </select>
              {itemSel && (
                <small className="muted">
                  Saldo disponível: <strong>{saldo}</strong> {itemSel.unidade ?? ""}
                </small>
              )}
            </label>

            <label className="field">
              <span>Quantidade <span className="req">*</span></span>
              <input type="number" min="0" step="any" value={quantidade} onChange={(e) => setQuantidade(e.target.value)} />
            </label>

            <label className="field">
              <span>Tipo do item <span className="req">*</span></span>
              <select value={tipoItem} onChange={(e) => setTipoItem(e.target.value as any)}>
                <option value="Retornável">Retornável (volta ao estoque)</option>
                <option value="Consumível">Consumível (não volta)</option>
              </select>
            </label>

            <label className="field">
              <span>Colaborador (quem pega) <span className="req">*</span></span>
              <input value={colaborador} onChange={(e) => setColaborador(e.target.value)} placeholder="Nome do colaborador" />
            </label>

            <label className="field">
              <span>Responsável (almoxarife)</span>
              <input value={responsavel} onChange={(e) => setResponsavel(e.target.value)} />
            </label>

            <label className="field">
              <span>Data da entrega <span className="req">*</span></span>
              <input type="date" value={dataEntrega} onChange={(e) => setDataEntrega(e.target.value)} />
            </label>

            <label className="field">
              <span>Nro da NF</span>
              <input value={notaFiscal} onChange={(e) => setNotaFiscal(e.target.value)} placeholder="Nota fiscal (se houver)" />
            </label>

            <label className="field">
              <span>Matrícula do solicitante</span>
              <input value={matricula} onChange={(e) => setMatricula(e.target.value)} />
            </label>

            <label className="field">
              <span>Autorizado por (Responsável OP)</span>
              <input value={autorizadoPor} onChange={(e) => setAutorizadoPor(e.target.value)} />
            </label>
          </div>

          <PhotoCapture label="Foto do item" value={foto} onChange={setFoto} />
          <GaleriaFotos fotos={fotos} onChange={setFotos} />
          <SignaturePad label="Assinatura do responsável" onChange={setAssResp} />
          <SignaturePad label="Assinatura do colaborador" onChange={setAssColab} />

          <label className="field field-wide">
            <span>Observação</span>
            <textarea rows={2} value={observacao} onChange={(e) => setObservacao(e.target.value)} />
          </label>

          {erro && <div className="form-error">{erro}</div>}

          <div className="modal-foot">
            <button type="button" className="btn-secondary" onClick={onClose}>
              Cancelar
            </button>
            <button type="submit" className="btn-primary" disabled={saving}>
              {saving ? "Salvando..." : textoBotao}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ==================== Modal: registrar devolução ==================== */
function DevolverModal({
  doc,
  onClose,
  onSaved,
}: {
  doc: DocumentoLista;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [dataDevolucao, setDataDevolucao] = useState(hojeLocal());
  const [observacao, setObservacao] = useState("");
  const [foto, setFoto] = useState("");
  const [assColab, setAssColab] = useState("");
  const [assResp, setAssResp] = useState("");
  const [saving, setSaving] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setErro(null);
    if (!foto) return setErro("Capture a foto da devolução do item.");
    if (!assColab) return setErro("Capture a assinatura do colaborador.");
    if (!assResp) return setErro("Capture a assinatura do responsável.");

    setSaving(true);
    try {
      await api.documentos.devolver(doc.id, {
        data_devolucao: dataDevolucao,
        observacao: observacao.trim(),
        foto_devolucao: foto,
        assinatura_dev_colaborador: assColab,
        assinatura_dev_responsavel: assResp,
      });
      onSaved();
    } catch (err) {
      setErro(err instanceof ApiError ? err.message : "Erro ao registrar a devolução.");
      setSaving(false);
    }
  }

  return (
    <div className="modal-overlay" onMouseDown={onClose}>
      <div className="modal-card modal-doc" onMouseDown={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <h3>Registrar devolução — documento #{doc.id}</h3>
          <button type="button" className="icon-btn" onClick={onClose}>
            <IconClose size={18} />
          </button>
        </div>
        <form className="modal-body doc-form" onSubmit={submit}>
          <div className="doc-resumo">
            <strong>{doc.item_descricao}</strong> · {doc.quantidade} {doc.item_unidade ?? ""} · entregue a{" "}
            <strong>{doc.colaborador}</strong> em {fmtData(doc.data_entrega)}
          </div>

          <label className="field">
            <span>Data da devolução <span className="req">*</span></span>
            <input type="date" value={dataDevolucao} onChange={(e) => setDataDevolucao(e.target.value)} />
          </label>

          <PhotoCapture label="Foto da devolução do item" value={foto} onChange={setFoto} />
          <SignaturePad label="Assinatura do colaborador" onChange={setAssColab} />
          <SignaturePad label="Assinatura do responsável" onChange={setAssResp} />

          <label className="field field-wide">
            <span>Observação</span>
            <textarea rows={2} value={observacao} onChange={(e) => setObservacao(e.target.value)} />
          </label>

          {erro && <div className="form-error">{erro}</div>}

          <div className="modal-foot">
            <button type="button" className="btn-secondary" onClick={onClose}>
              Cancelar
            </button>
            <button type="submit" className="btn-primary" disabled={saving}>
              {saving ? "Salvando..." : "Confirmar devolução"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ==================== Modal: ver documento ==================== */
function VerDocumentoModal({ id, onClose }: { id: number; onClose: () => void }) {
  const [doc, setDoc] = useState<DocumentoFull | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    let cancel = false;
    api.documentos
      .get(id)
      .then((d) => !cancel && setDoc(d))
      .catch((e) => !cancel && setErro(e instanceof ApiError ? e.message : "Erro ao carregar."));
    return () => {
      cancel = true;
    };
  }, [id]);

  return (
    <div className="modal-overlay" onMouseDown={onClose}>
      <div className="modal-card modal-doc" onMouseDown={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <h3>Documento #{id}</h3>
          <button type="button" className="icon-btn" onClick={onClose}>
            <IconClose size={18} />
          </button>
        </div>
        <div className="modal-body">
          {erro ? (
            <div className="form-error">{erro}</div>
          ) : !doc ? (
            <div className="empty">Carregando...</div>
          ) : (
            <div className="doc-view">
              <div className="doc-meta">
                <div><span className="muted">Item</span><strong>{doc.item_codigo} — {doc.item_descricao}</strong></div>
                <div><span className="muted">Quantidade</span><strong>{doc.quantidade} {doc.item_unidade ?? ""}</strong></div>
                <div><span className="muted">Tipo</span><strong>{doc.tipo_item}</strong></div>
                <div><span className="muted">Colaborador</span><strong>{doc.colaborador}</strong></div>
                <div><span className="muted">Responsável</span><strong>{doc.responsavel ?? "—"}</strong></div>
                <div>
                  <span className="muted">Status</span>
                  <strong><span className={`pill ${STATUS_PILL[doc.status] ?? "pill-neutral"}`}>{doc.status}</span></strong>
                </div>
                {doc.observacao && (
                  <div className="doc-meta-wide"><span className="muted">Observação</span><strong>{doc.observacao}</strong></div>
                )}
              </div>

              <div className="doc-secao">
                <h4>Entrega — {fmtData(doc.data_entrega)}</h4>
                <div className="doc-imgs">
                  <ImgBox titulo="Foto do item" src={doc.foto_entrega} />
                  <ImgBox titulo="Assinatura do responsável" src={doc.assinatura_responsavel} assinatura />
                  <ImgBox titulo="Assinatura do colaborador" src={doc.assinatura_colaborador} assinatura />
                </div>
              </div>

              {doc.status === "Devolvido" && (
                <div className="doc-secao">
                  <h4>Devolução — {fmtData(doc.data_devolucao)}</h4>
                  <div className="doc-imgs">
                    <ImgBox titulo="Foto da devolução" src={doc.foto_devolucao} />
                    <ImgBox titulo="Assinatura do colaborador" src={doc.assinatura_dev_colaborador} assinatura />
                    <ImgBox titulo="Assinatura do responsável" src={doc.assinatura_dev_responsavel} assinatura />
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ImgBox({ titulo, src, assinatura }: { titulo: string; src: string | null; assinatura?: boolean }) {
  return (
    <div className="doc-imgbox">
      <span className="muted">{titulo}</span>
      {src ? (
        <img src={src} alt={titulo} className={assinatura ? "doc-img doc-img-sig" : "doc-img"} />
      ) : (
        <div className="doc-img doc-img-vazio">—</div>
      )}
    </div>
  );
}
